import { useState, useRef, useEffect, useMemo } from 'react';
import { Bell, User, Settings, CreditCard, HelpCircle, LogOut, Calendar, Trophy, Menu, Home, Sun, Moon, QrCode } from './Icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI, customerSubscriptionsAPI } from '../services/api';
import { format, parseISO, isToday, isPast, isFuture } from 'date-fns';
import NextVideoCountdown from './NextVideoCountdown';
import { useIsMobile } from '../hooks/useIsMobile';

// Rotating greetings based on time of day
const getGreeting = (firstName) => {
  const hour = new Date().getHours();
  const name = firstName || 'Member';

  // Time-based greetings
  const morningGreetings = [
    { prefix: 'Good morning', name },
    { prefix: 'Rise and shine', name },
    { prefix: 'Morning', name },
    { prefix: 'Top of the morning', name },
    { prefix: 'Early bird gets the gains', name },
  ];

  const afternoonGreetings = [
    { prefix: 'Good afternoon', name },
    { prefix: 'Hey there', name },
    { prefix: 'Hello', name },
    { prefix: "What's up", name },
    { prefix: 'Afternoon vibes', name },
  ];

  const eveningGreetings = [
    { prefix: 'Good evening', name },
    { prefix: 'Evening', name },
    { prefix: 'Hey', name },
    { prefix: 'Hello again', name },
    { prefix: 'Night owl mode', name },
  ];

  // General greetings (any time)
  const generalGreetings = [
    { prefix: 'Welcome back', name },
    { prefix: 'Hey', name },
    { prefix: 'Hi', name },
    { prefix: 'Great to see you', name },
    { prefix: 'Ready to crush it', name },
    { prefix: "Let's do this", name },
    { prefix: 'Looking good', name },
    { prefix: "Glad you're here", name },
    { prefix: 'Time to shine', name },
    { prefix: "You've got this", name },
  ];

  // Pick based on time of day
  let timeBasedPool;
  if (hour >= 5 && hour < 12) {
    timeBasedPool = morningGreetings;
  } else if (hour >= 12 && hour < 17) {
    timeBasedPool = afternoonGreetings;
  } else {
    timeBasedPool = eveningGreetings;
  }

  // Combine time-based with general for more variety
  const allGreetings = [...timeBasedPool, ...generalGreetings];

  // Use date-based seed for consistency within the same session/day
  const today = new Date().toDateString();
  const seed = today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = seed % allGreetings.length;

  return allGreetings[index];
};

// Theme toggle hook
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return { theme, toggleTheme };
}

export default function CustomerHeader({ onMenuClick, onCheckInClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  // Fetch user's bookings for notifications
  const { data: bookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false
  });

  // Fetch subscription info
  const { data: subscriptionData } = useQuery({
    queryKey: ['customer-subscription'],
    queryFn: () => customerSubscriptionsAPI.getSubscription(),
    enabled: !!user?.id,
    retry: false
  });

  const bookings = bookingsData?.data || [];
  const subscription = subscriptionData?.data?.customer;

  // Generate customer-specific notifications
  const notifications = (() => {
    const notifs = [];

    // Check for classes today
    const classesToday = bookings.filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const classDate = parseISO(booking.schedule.specificDate);
      return isToday(classDate);
    });

    if (classesToday.length > 0) {
      notifs.push({
        id: 1,
        text: `You have ${classesToday.length} ${classesToday.length === 1 ? 'class' : 'classes'} today!`,
        time: 'Today',
        unread: true,
        icon: Calendar
      });
    }

    // Check for recently completed classes (past 24 hours)
    // Use snapshot classDate first (survives schedule deletion)
    const recentlyCompleted = bookings.filter(booking => {
      const rawDate = booking.classDate || booking.schedule?.specificDate;
      if (!rawDate) return false;
      const classDate = parseISO(rawDate);
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return isPast(classDate) && !isToday(classDate) && classDate >= yesterday;
    });

    if (recentlyCompleted.length > 0) {
      notifs.push({
        id: 2,
        text: `Great job! You completed ${recentlyCompleted.length} ${recentlyCompleted.length === 1 ? 'class' : 'classes'}!`,
        time: 'Yesterday',
        unread: true,
        icon: Trophy
      });
    }

    // Subscription status
    if (subscription?.hasPaidSubscription) {
      notifs.push({
        id: 3,
        text: `Your ${subscription.monthlyAmount ? `$${subscription.monthlyAmount}/month` : ''} subscription is active`,
        time: 'Active',
        unread: false,
        icon: CreditCard
      });
    }

    return notifs;
  })();

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasUnreadNotifications = notifications.some(n => n.unread);

  // Dropdown styles for consistency
  const dropdownStyles = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    boxShadow: theme === 'dark' ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 40px rgba(0,0,0,0.12)',
  };

  const dropdownItemStyles = {
    color: 'var(--text-secondary)',
  };

  return (
    <header
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, rgba(31,41,55,0.95) 0%, rgba(17,24,39,0.98) 100%)'
          : 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'var(--border-muted)'}`,
        boxShadow: theme === 'dark' ? '0 4px 20px rgba(0,0,0,0.15)' : '0 4px 20px rgba(0,0,0,0.05)',
        position: 'relative',
      }}
    >
      {/* Subtle gradient accent line at top - peach primary */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 1,
        background: `linear-gradient(90deg, transparent, var(--primary-30), ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}, var(--primary-30), transparent)`,
      }} />
      {/* Claude sample uses padding: 20px 32px */}
      <div className="px-4 md:px-8 py-4 md:py-5">
        {/* Mobile Layout */}
        {isMobile ? (
          <div className="flex items-center justify-between">
            {/* Left: Hamburger Menu */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg transition-all duration-200"
              style={{ color: 'var(--text-muted)' }}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Center: Tappable Dashboard Link */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg py-1 px-2 transition-all duration-200"
            >
              <Home className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <span
                className="text-base font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Hey, <span style={{ color: 'var(--primary)' }}>{user?.firstName || 'there'}</span>!
              </span>
            </button>

            {/* Right: Check In, Theme Toggle, Profile */}
            <div className="flex items-center gap-2">
              {/* Check In Button - Sage Green */}
              <button
                onClick={onCheckInClick}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-200"
                style={{
                  background: 'linear-gradient(145deg, var(--secondary-15) 0%, var(--secondary-8) 100%)',
                  border: '1px solid var(--secondary-30)',
                  color: 'var(--secondary)',
                }}
                aria-label="Scan QR to check in"
              >
                <QrCode className="w-4 h-4" />
                <span className="text-xs font-semibold">Scan QR</span>
              </button>

              {/* Notifications Icon Only - Disabled for now */}
              {/* <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg transition-all duration-200"
                  style={{ color: 'var(--text-muted)' }}
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {hasUnreadNotifications && (
                    <span
                      className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full animate-pulse-custom"
                      style={{ background: 'var(--primary)' }}
                    />
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="absolute right-0 mt-2 w-80 rounded-xl py-2 z-50"
                    style={dropdownStyles}
                  >
                    <div
                      className="px-4 py-2"
                      style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    >
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Notifications
                      </h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => {
                          const Icon = notif.icon || Bell;
                          return (
                            <div
                              key={notif.id}
                              className="px-4 py-3 cursor-pointer transition-all duration-200"
                              style={{
                                background: notif.unread ? 'var(--primary-5)' : 'transparent',
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: notif.unread ? 'var(--primary-15)' : 'var(--bg-elevated)',
                                  }}
                                >
                                  <Icon
                                    className="w-4 h-4"
                                    style={{ color: notif.unread ? 'var(--primary)' : 'var(--text-muted)' }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {notif.text}
                                  </p>
                                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                    {notif.time}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--text-faint)' }} />
                          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div> */}

              {/* Profile Icon Only */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
                  style={{
                    background: 'var(--secondary-15)',
                    color: 'var(--secondary)',
                  }}
                  aria-label="Profile menu"
                >
                  <User className="w-5 h-5" />
                </button>

                {/* Profile Dropdown */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-xl py-2 z-50"
                    style={dropdownStyles}
                  >
                    <div
                      className="px-4 py-3"
                      style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    >
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Settings</span>
                    </button>
                    {/* Subscription - Disabled for now */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/subscription');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <CreditCard className="w-4 h-4" />
                      <span className="text-sm">Subscription</span>
                    </button> */}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/classes');
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">My Classes</span>
                    </button>
                    <button
                      onClick={toggleTheme}
                      className="w-full px-4 py-2 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                      <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 8, paddingTop: 8 }}>
                      <button
                        onClick={logout}
                        className="w-full px-4 py-2 text-left flex items-center gap-3 transition-all duration-200"
                        style={{ color: '#ef4444' }}
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Desktop Layout - Match Claude's sample spacing */
          <div className="flex items-center justify-between">
            <div>
              <h1
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  marginBottom: 4,
                  letterSpacing: '-0.5px',
                  color: 'var(--text-primary)',
                }}
              >
                {(() => {
                  const greeting = getGreeting(user?.firstName);
                  return <>{greeting.prefix}, <span style={{ color: 'var(--primary)' }}>{greeting.name}!</span></>;
                })()}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {subscription?.hasPaidSubscription
                  ? `Active Subscription • $${parseFloat(subscription.monthlyAmount || 0).toFixed(2)}/month`
                  : 'Member'}
              </p>
            </div>

            <div className="flex items-center gap-5">
              {/* Check In Button - Hidden on desktop (only useful on mobile for QR scanning) */}

              {/* Notifications - Disabled for now */}
              {/* <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 rounded-xl transition-all duration-200"
                  style={{
                    background: 'var(--highlight-10)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Bell className="w-5 h-5" />
                  {hasUnreadNotifications && (
                    <span
                      className="absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse-custom"
                      style={{ background: 'var(--primary)' }}
                    />
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="absolute right-0 mt-2 w-80 rounded-xl py-2 z-50"
                    style={dropdownStyles}
                  >
                    <div
                      className="px-4 py-2"
                      style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    >
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Notifications
                      </h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => {
                          const Icon = notif.icon || Bell;
                          return (
                            <div
                              key={notif.id}
                              className="px-4 py-3 cursor-pointer transition-all duration-200"
                              style={{
                                background: notif.unread ? 'var(--primary-5)' : 'transparent',
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: notif.unread ? 'var(--primary-15)' : 'var(--bg-elevated)',
                                  }}
                                >
                                  <Icon
                                    className="w-4 h-4"
                                    style={{ color: notif.unread ? 'var(--primary)' : 'var(--text-muted)' }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {notif.text}
                                  </p>
                                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                    {notif.time}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--text-faint)' }} />
                          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div> */}

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <div
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-all duration-200"
                  style={{
                    background: 'var(--secondary-8)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{
                      background: 'var(--secondary-20)',
                    }}
                  >
                    <User className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
                  </div>
                  <div className="text-left pr-1">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {user?.firstName} {user?.lastName?.charAt(0)}.
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Member</p>
                  </div>
                </div>

                {/* Profile Dropdown */}
                {showProfileMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-xl py-2 z-50"
                    style={dropdownStyles}
                  >
                    <div
                      className="px-4 py-3"
                      style={{ borderBottom: '1px solid var(--border-subtle)' }}
                    >
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Settings</span>
                    </button>
                    {/* Subscription - Disabled for now */}
                    {/* <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/subscription');
                      }}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <CreditCard className="w-4 h-4" />
                      <span className="text-sm">Subscription</span>
                    </button> */}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/classes');
                      }}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">My Classes</span>
                    </button>
                    <button
                      onClick={toggleTheme}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-all duration-200"
                      style={dropdownItemStyles}
                    >
                      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                      <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 8, paddingTop: 8 }}>
                      <button
                        onClick={logout}
                        className="w-full px-4 py-2.5 text-left flex items-center gap-3 transition-all duration-200"
                        style={{ color: '#ef4444' }}
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <NextVideoCountdown />
    </header>
  );
}
